import { useMemo, useState } from "react";
import {
  addDays,
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { pl } from "date-fns/locale";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { formatDate } from "@/utils/formatDate";
import type { Occurrence } from "../types";

type ViewMode = "month" | "week";

interface Props {
  occurrences: Occurrence[];
  /** Visible range changed — reload the feed. */
  onRangeChange: (fromISO: string, toISO: string) => void;
  onPickOccurrence: (occurrence: Occurrence) => void;
  onPickSlot?: (day: Date) => void;
}

const STATUS_CHIP: Record<string, string> = {
  scheduled: "bg-blue-600/90 text-white",
  live: "bg-red-600 text-white",
  ended: "bg-gray-400/80 text-white line-through",
  cancelled: "bg-amber-500/80 text-white line-through",
};

function rangeFor(anchor: Date, mode: ViewMode): { start: Date; end: Date } {
  if (mode === "week") {
    return {
      start: startOfWeek(anchor, { weekStartsOn: 1 }),
      end: endOfWeek(anchor, { weekStartsOn: 1 }),
    };
  }
  return {
    start: startOfWeek(startOfMonth(anchor), { weekStartsOn: 1 }),
    end: endOfWeek(endOfMonth(anchor), { weekStartsOn: 1 }),
  };
}

export function MeetingsCalendar({
  occurrences,
  onRangeChange,
  onPickOccurrence,
  onPickSlot,
}: Props) {
  const [anchor, setAnchor] = useState<Date>(new Date());
  const [mode, setMode] = useState<ViewMode>("month");

  const { start, end } = useMemo(() => rangeFor(anchor, mode), [anchor, mode]);
  const days = useMemo(() => eachDayOfInterval({ start, end }), [start, end]);

  const byDay = useMemo(() => {
    const map = new Map<string, Occurrence[]>();
    for (const occ of occurrences) {
      const key = format(new Date(occ.starts_at), "yyyy-MM-dd");
      const list = map.get(key) ?? [];
      list.push(occ);
      map.set(key, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.starts_at.localeCompare(b.starts_at));
    }
    return map;
  }, [occurrences]);

  const navigate = (direction: -1 | 1) => {
    const next =
      mode === "month" ? addMonths(anchor, direction) : addWeeks(anchor, direction);
    setAnchor(next);
    const r = rangeFor(next, mode);
    onRangeChange(format(r.start, "yyyy-MM-dd"), format(r.end, "yyyy-MM-dd"));
  };

  const switchMode = (m: ViewMode) => {
    setMode(m);
    const r = rangeFor(anchor, m);
    onRangeChange(format(r.start, "yyyy-MM-dd"), format(r.end, "yyyy-MM-dd"));
  };

  const weekdayHeaders = useMemo(
    () =>
      eachDayOfInterval({
        start: startOfWeek(new Date(), { weekStartsOn: 1 }),
        end: addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), 6),
      }).map((d) => format(d, "EEE", { locale: pl })),
    [],
  );

  return (
    <div className="flex flex-col rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
      <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="rounded p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
          >
            <ChevronLeft size={18} />
          </button>
          <span className="min-w-[11rem] text-center text-sm font-semibold capitalize text-[var(--color-text-primary)]">
            {mode === "month"
              ? format(anchor, "LLLL yyyy", { locale: pl })
              : `${formatDate(start)} – ${formatDate(end)}`}
          </span>
          <button
            type="button"
            onClick={() => navigate(1)}
            className="rounded p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
          >
            <ChevronRight size={18} />
          </button>
        </div>
        <div className="flex overflow-hidden rounded-md border border-[var(--color-border)] text-xs">
          {(["month", "week"] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => switchMode(m)}
              className={`px-3 py-1.5 ${
                mode === m
                  ? "bg-[var(--color-primary)] text-white"
                  : "text-[var(--color-text-secondary)] hover:bg-black/5 dark:hover:bg-white/5"
              }`}
            >
              {m === "month" ? "Miesiąc" : "Tydzień"}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-7 border-b border-[var(--color-border)] text-center text-xs font-medium uppercase text-[var(--color-text-muted)]">
        {weekdayHeaders.map((label) => (
          <div key={label} className="py-1.5">
            {label}
          </div>
        ))}
      </div>

      <div className={`grid grid-cols-7 ${mode === "week" ? "" : "auto-rows-fr"}`}>
        {days.map((day) => {
          const key = format(day, "yyyy-MM-dd");
          const items = byDay.get(key) ?? [];
          const dimmed = mode === "month" && !isSameMonth(day, anchor);
          return (
            <div
              key={key}
              onClick={() => onPickSlot?.(day)}
              className={`min-h-[6rem] cursor-pointer border-b border-r border-[var(--color-border)] p-1 align-top transition-colors hover:bg-black/[.03] dark:hover:bg-white/[.04] ${
                dimmed ? "opacity-40" : ""
              } ${mode === "week" ? "min-h-[14rem]" : ""}`}
            >
              <div
                className={`mb-1 flex h-5 w-5 items-center justify-center rounded-full text-xs ${
                  isToday(day)
                    ? "bg-[var(--color-primary)] font-bold text-white"
                    : "text-[var(--color-text-secondary)]"
                }`}
              >
                {format(day, "d")}
              </div>
              <div className="space-y-0.5">
                {items.slice(0, mode === "week" ? 12 : 4).map((occ) => (
                  <button
                    key={occ.id}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPickOccurrence(occ);
                    }}
                    title={occ.title}
                    className={`block w-full truncate rounded px-1.5 py-0.5 text-left text-[11px] leading-tight ${
                      STATUS_CHIP[occ.status] ?? STATUS_CHIP.scheduled
                    }`}
                  >
                    {format(new Date(occ.starts_at), "HH:mm")} {occ.title}
                  </button>
                ))}
                {items.length > (mode === "week" ? 12 : 4) && (
                  <div className="px-1 text-[10px] text-[var(--color-text-muted)]">
                    +{items.length - (mode === "week" ? 12 : 4)} więcej
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function isSameCalendarDay(a: Date, b: Date): boolean {
  return isSameDay(a, b);
}
